import { store as createStore, autoEffect } from '@risingstack/react-easy-state'

import api from '../api'
import { read, write } from '../utilities/local-storage'
import { debug } from '../utilities/logger'
import { isCityName, isPostalCode, isSponsorCode } from '../utilities/validators'
import { CityAndPostalCodeSearch } from '../api/externals'

/**
 * [Store]
 */
const store = createStore({
  loading: true,
  fromJardiniers: false,
  gardeners: [],
  ratings: {},
  cityDescriptionHtml: null,
  titleHtml: null,
  metaDescription: null,
  metaTitle: null,
  pictureList: [],
  selected: [],
  search: {
    postalCode: null,
    allPostalCodesOfCity: null,
    city: null,
    sponsor: null,
    proBusinessReferral: null,
    entryFunnel: null
  },
  gardener: null,
  initialized: false,
  initialize,
  loadCache,
  enableAutoPersist,
  setQuery,
  setSearch,
  doSearch,
  getGardener,
  selectAll,
  deselectAll,
  toggle,
  isEmpty,
  setProBusinessReferral,
  clearProBusinessReferral,
  setEntryFunnel
})

/**
 * [Cache management]
 */
const SEARCH_STORAGE_KEY = 'stores.search.search'

function load () { return read(SEARCH_STORAGE_KEY) }

function persist () { write(SEARCH_STORAGE_KEY, { search: store.search, selected: store.selected }) }

async function loadCache () {
  debug('[store][search.loadCache]')
  const cache = load()
  store.search = { ...store.search, ...cache.search }
  store.selected = cache.selected || []
}

function enableAutoPersist () {
  autoEffect(() => {
    debug('[store][search.autoeffect]')
    persist()
  })
}

function initialize () {
  if (store.initialized) { return }
  loadCache()
  enableAutoPersist()
  store.initialized = true
}

/**
 * [Functions]
 */

/**
 * Search strategies
 * As one input can give various types of searchs depending on the format
 */
const SEARCH_STRATEGIES = [
  {
    accepts: isPostalCode,
    run: (zipCode) => api.externals.PostalCodeToCity(zipCode).then((res) => {
      return {
        postalCode: zipCode,
        city: res.name,
        allPostalCodesOfCity: res.allPostalCodesOfCity
      }
    })
  },
  {
    accepts: isSponsorCode,
    run: (sponsor) => {
      debug(`[store][search.setQuery] Sponsor code detected: ${sponsor}`)
      return Promise.resolve({ sponsor: sponsor })
    }
  },
  {
    accepts: isCityName,
    run: (cityName) => api.externals.CityToPostalCode(cityName).then((res) => {
      return (res.allPostalCodesOfCity && res.allPostalCodesOfCity.length === 1)
        ? {
          postalCode: res.allPostalCodesOfCity[0],
          city: res.name,
          allPostalCodesOfCity: res.allPostalCodesOfCity
        } : {
          city: res.name,
          allPostalCodesOfCity: res.allPostalCodesOfCity
        }
    })
  },
  {
    // Fallback for other cases
    accepts: () => true,
    run: (sponsor) => {
      debug(`[store][search.setQuery] Fallback sponsor: ${sponsor}`)
      return Promise.resolve({ sponsor: sponsor })
    }
  }
]

async function doSearch (customerId) {
  store.loading = true
  const code = store.search.sponsor || store.search.postalCode
  const cityName = store.search.sponsor ? null : store.search.city
  let gardeners = []
  let cityDescriptionHtml = null
  let titleHtml = null
  let metaDescription = null
  let metaTitle = null
  let pictureList = []

  // Determine which postal codes to send to the API
  const allPostalCodes = store.search.allPostalCodesOfCity || []
  const isFirstPostalCode = code && allPostalCodes.length > 1 && code === allPostalCodes[0]
  const codes = isFirstPostalCode ? allPostalCodes : (code ? [code] : allPostalCodes)

  debug(`[store][search.doSearch] Searching with codes: ${codes} (isFirstPostalCode: ${isFirstPostalCode})`)

  try {
    const response = await api.gardeners.search({
      codes: codes,
      cityName: cityName,
      customerId
    })
    gardeners = response.professionalsPage.content
    cityDescriptionHtml = response.resultHtml
    titleHtml = response.titleHtml
    metaDescription = response.metaDescription
    metaTitle = response.metaTitle
    pictureList = response.pictureList
  } catch (e) {
    debug(`[store][search.doSearch] An error occured when searching for ${codes}. ${e}`)
  }

  // Filter out the referring pro when coming from a pro_apport link
  // The client should not be able to request the pro who referred them
  // Exception: ATJ employees (salariés CJM) can self-refer
  const ATJ_EMPLOYEE_REFERRAL_CODES = ['ke54', 'bz88', 'cr36', 'ty34']
  const proBusinessReferral = store.search.proBusinessReferral
  if (proBusinessReferral && gardeners.length > 0) {
    const originalCount = gardeners.length
    gardeners = gardeners.filter(function (gardener) {
      // Compare with professionalReferralCode (case-insensitive)
      const gardenerCode = gardener.professionalReferralCode
      if (!gardenerCode) return true
      if (gardenerCode.toLowerCase() === proBusinessReferral.toLowerCase()) {
        // Allow ATJ employees to appear in their own referral results
        return ATJ_EMPLOYEE_REFERRAL_CODES.includes(gardenerCode.toLowerCase())
      }
      return true
    })
    if (gardeners.length < originalCount) {
      debug(`[store][search.doSearch] Filtered out referring pro (code: ${proBusinessReferral}). ${originalCount} -> ${gardeners.length} gardeners`)
    }
  }

  // Ratings shown on the cards, resolved before the cards are displayed so the listing does not
  // shift when they land. Never fatal: no ratings simply means no stars.
  let ratings = {}
  const ratingIds = gardeners.map((gardener) => gardener.id).filter((id) => !!id)
  if (ratingIds.length) {
    try {
      ratings = await api.gardeners.getBulkRatings({ ids: ratingIds })
    } catch (e) {
      debug(`[store][search.doSearch] Ratings unavailable, cards will show no stars. ${e}`)
    }
  }

  store.loading = false
  store.gardeners = gardeners
  store.ratings = ratings
  store.cityDescriptionHtml = cityDescriptionHtml
  store.titleHtml = titleHtml
  store.metaDescription = metaDescription
  store.metaTitle = metaTitle
  store.pictureList = pictureList
}

async function getGardener ({ externalId }) {
  try {
    const response = await api.gardeners.get({ externalId })
    store.gardener = response
  } catch (error) {
    debug('[search][getGardener] Error fetching gardener:', error.message || error)
    store.gardener = null
  }
}

// async function setSearch ({ customerId, postalCode, city, sponsor, allPostalCodesOfCity } = store.search) {
//   if ((store.search.postalCode === postalCode) && store.gardeners.length) {
//     return
//   }
//   store.search.postalCode = postalCode
//   store.search.allPostalCodesOfCity = allPostalCodesOfCity
//   store.search.city = city
//   store.search.sponsor = sponsor
//   await doSearch(customerId)
// }

async function setQuery ({ code1, code2, customerId }) {
  // Nettoyage et validation des entrées
  const cleanCode1 = code1 ? code1.toString().trim() : ''
  const cleanCode2 = code2 ? code2.toString().trim() : ''

  debug(`[store][search.setQuery] Input: code1="${cleanCode1}", code2="${cleanCode2}"`)

  if (cleanCode1 && cleanCode2) {
    // Case with city AND zip code
    if (isCityName(cleanCode1) && isPostalCode(cleanCode2)) {
      debug(`[store][search.setQuery] City + PostalCode search: ${cleanCode1} + ${cleanCode2}`)
      const res = await CityAndPostalCodeSearch({ city: cleanCode1, zipCode: cleanCode2 })
      if (res && res.name && res.postalCode) {
        await setSearch({
          ...res,
          city: res.name,
          postalCode: res.postalCode,
          customerId
        })
        return
      }
      debug(`[store][search.setQuery] City + PostalCode search failed, trying postal code only`)
      // If the combined search fails, try using the zip code alone.
      await setQuery({ code1: cleanCode2, customerId })
      return
    } else if (isCityName(cleanCode2) && isPostalCode(cleanCode1)) {
      debug(`[store][search.setQuery] PostalCode + City search: ${cleanCode1} + ${cleanCode2}`)
      const res = await CityAndPostalCodeSearch({ city: cleanCode2, zipCode: cleanCode1 })
      if (res && res.name && res.postalCode) {
        await setSearch({
          ...res,
          city: res.name,
          postalCode: res.postalCode,
          customerId
        })
        return
      }
      debug(`[store][search.setQuery] PostalCode + City search failed, trying postal code only`)
      // If the combined search fails, try using the zip code alone.
      await setQuery({ code1: cleanCode1, customerId })
      return
    }
  }

  // Cas avec un seul paramètre
  if (cleanCode1) {
    debug(`[store][search.setQuery] Single parameter search: ${cleanCode1}`)
    const strategy = SEARCH_STRATEGIES.find((strategy) => strategy.accepts(cleanCode1))
    if (strategy) {
      try {
        const search = await strategy.run(cleanCode1)
        if (search && (search.city || search.sponsor || search.postalCode)) {
          await setSearch({ ...search, customerId })
          debug(`[store][search.setQuery] Single parameter search successful`)
          return
        }
      } catch (error) {
        debug(`[store][search.setQuery] Strategy error: ${error}`)
      }
    }
  }

  // Failure: do not modify state if nothing found
  debug(`[store][search.setQuery] No valid search result found`)
}

async function setSearch ({ customerId, postalCode, city, sponsor, allPostalCodesOfCity } = store.search) {
  if ((store.search.postalCode === postalCode) && store.gardeners.length) {
    return // Avoid redundant searches
  }

  store.search.postalCode = postalCode
  store.search.allPostalCodesOfCity = allPostalCodesOfCity
  store.search.city = city
  store.search.sponsor = sponsor
  await doSearch(customerId)
}

function selectAll () {
  this.selected = store.gardeners.map((g) => g.id)
}

function deselectAll () {
  this.selected = []
}

function toggle ({ id }) {
  if (this.selected.indexOf(id) === -1) {
    this.selected.push(id)
  } else {
    this.selected = this.selected.filter((i) => i !== id)
  }
}

function isEmpty () {
  return !store.search.sponsor && !store.search.postalCode
}

/**
 * Pro Business Referral (Apport d'affaire L2)
 * Stores the referral code from a professional for commission tracking.
 * This is separate from the sponsor system (which creates L1 relationships).
 */
function setProBusinessReferral (code) {
  if (code) {
    const cleanCode = code.toString().trim().toLowerCase()
    debug(`[store][search.setProBusinessReferral] Setting pro business referral code: ${cleanCode}`)
    store.search.proBusinessReferral = cleanCode
  }
}

function clearProBusinessReferral () {
  debug('[store][search.clearProBusinessReferral] Clearing pro business referral code')
  store.search.proBusinessReferral = null
}

/**
 * Entry funnel (lead qualification)
 * Captured where the customer enters the site (WordPress postal code search or /jardiniers pages),
 * persisted with the search so it is still known when the inquiry is submitted.
 */
function setEntryFunnel (entryFunnel) {
  debug(`[store][search.setEntryFunnel] Setting entry funnel: ${entryFunnel}`)
  store.search.entryFunnel = entryFunnel
}

export default store



// WEBPACK FOOTER //
// ./src/stores/search.js